<template>
  <div>
    <v-container grid-list-lg>
      <v-layout>
        <v-flex>
          <h1 class="display-1">
            {{ $t('caseView.title') }}
            <small class="grey--text text--darken-1">
              {{ courtCase.caseNumber }}
            </small>
          </h1>
        </v-flex>
      </v-layout>
      <case-view-details :courtCase="courtCase" :dynamicCaseFields="dynamicCaseFields"/>
      <v-layout v-for="(table,name) in tableData"
                :key="name + '_table'"
                class="mt-4">
        <v-flex xs12>
          <case-summary-table
            :table="table"/>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import sortBy from 'lodash/sortBy';
import filter from 'lodash/filter';
import partition from 'lodash/partition';
import PAUTIL from '@/support/utility';
import PACONST from '@/support/constants';
import Cove from '@ctrack/cove';
import axios from 'axios';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import CaseSummaryTable from '@viewsupport/CaseSummaryTable';
import CaseViewDetails from '@viewsupport/CaseViewDetails';
import { getHeaderAlignmentClass } from '@/support/ui';

import { TaskQueue } from 'cwait';

export default {
  components: { CaseSummaryTable, CaseViewDetails },
  props: {
    caseID: {
      type: String,
      required: true
    }
  },
  methods: {
    getNestedProperty (obj, property) {
      return PAUTIL.resolvePath(obj, property);
    },
    updateTab (newTab) {
      this.selectedTab = newTab;
    }
  },
  data () {
    return {
      dynamicCaseFields: [],
      courtCase: {},
      tables: [],
      tableData: {},
      taskQueue: new TaskQueue(Promise, Cove.getConfig('api.bulk.maxConcurrentRequests'))
    };
  },
  computed: {
    ...mapGetters('user', ['hasAccess']),
    _pageTitle () {
      return this.$t('caseView.title') + ' ' + this.courtCase.caseNumber;
    }
  },
  created () {
    function getCaseData (caseID) {
      let caseURL = Cove.getAPI({
        name: 'api.case',
        params: {
          caseID
        }
      });

      return axios.get(caseURL);
    }

    function getDynamicCaseFields (caseID) {
      let fieldsURL = Cove.getAPI({
        name: 'api.dynamicView.search',
        query: {
          caseID,
          viewID: PACONST.SYSTEM_CODE_TYPE_VIEW_TYPE
        }
      });

      return axios.get(fieldsURL);
    }

    let caseDataRequest = getCaseData(this.caseID);
    let caseFieldsRequest = getDynamicCaseFields(this.caseID);

    Promise.all([caseDataRequest, caseFieldsRequest]).then(([dataResponse, fieldResponse]) => {
      this.courtCase = dataResponse.data;

      let tables = sortBy(this.$PACONST.getTabsByCategory(this.courtCase.caseCategory), 'orderBy');
      tables = filter(tables, (table) =>
      {
        return this.hasAccess(table.componentID, this.$PACONST.PERMISSION_TYPES.READ);
      });

      let p = partition(tables, ['name', Cove.getConfig('caseView.firstTab')]);
      this.tables = p[0].concat(p[1]);

      this.dynamicCaseFields = sortBy(fieldResponse.data.viewFields, 'orderBy');

      this.tables.forEach((table) => {
        let fieldsURL = Cove.getAPI(
          { name: 'api.dynamicView.search', query: { caseID: this.caseID, viewID: table.viewID } });

        this.$set(this.tableData, table.name, { name: table.name, headers: [], data: [] });

        // build headers
        this.taskQueue.add(() => {
          return axios.get(fieldsURL).then((response) => {
            let rawFields = sortBy(response.data.viewFields, 'orderBy');
            let headers = [];

            rawFields.forEach((f) => {
              let header = {
                text: f.label,
                sortable: false,
                modelValue: f.modelValue,
                dataTypeValue: f.fieldDataTypeCode.viewFieldDataTypeValue,
                value: '',
                class: getHeaderAlignmentClass(f.fieldDataTypeCode.viewFieldDataTypeValue)
              };

              headers.push(header);
            });

            this.tableData[table.name].headers = Object.assign([], headers);

            let dataURL = Cove.getAPI({
              name: 'api.cases.table',
              params: { caseID: this.caseID, searchType: table.name.toLowerCase() }
            });
            let pagination = new Cove.Pagination( { rowsPerPage: this.$PACONST.MAX_SUMMARY_RESULTS_LIMIT } );

            if (response.data.tableViewSorts !== undefined && response.data.tableViewSorts.length > 0)
            {
              let sorting = response.data.tableViewSorts[0];
              pagination.sortBy = sorting.sortProperty;
              pagination.descending = sorting.sortDirection.sortDirectionID ===
                this.$PACONST.SORT_DIRECTION_VALUE_DESC;
            }

            // get data
            this.taskQueue.add(() => {
              return axios.get(dataURL, { headers: pagination.generateHeaders() }).then((response) => {
                this.tableData[table.name].data = Object.assign([], response.data);

                pagination.parseHeaders(response);
                Vue.set(this.tableData[table.name], 'totalItems', pagination.totalItems);
              });
            });
          });
        });
      });
    });
  }
};
</script>

<!-- this can't be scoped or it won't style the header / footer -->
<style lang="scss">
dt {
  font-weight: 500;
  text-align: right;
}
.card-layout .flex {
  padding: 6px !important;
}

@media print {
  // hide header and footer
  .v-toolbar,
  .v-footer {
    display: none !important;
  }
  // slide content up into space the header was occupying
  main.v-content {
    padding-top: 0px !important;
  }
  // print links like all other text
  a {
    color: inherit !important;
    text-decoration: inherit !important;
  }
}
</style>
